import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Box, Button, Typography, Alert, CircularProgress, styled } from '@mui/material';
import { createPaymentIntent, confirmCardPayment } from '../../services/paymentService';

interface StripeCheckoutFormProps {
    onPaymentSuccess: (paymentIntentId: string) => void;
    onPaymentError: (errorMessage: string) => void;
    amount: number;
    productId: string;
    reservation: any;
}

const CardElementContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
    }
}));

const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
    hidePostalCode: true
};

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
    onPaymentSuccess,
    onPaymentError,
    amount,
    productId,
    reservation
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentAmount, setPaymentAmount] = useState<number>(amount);

    // Créer une intention de paiement au chargement du composant
    useEffect(() => {
        const fetchPaymentIntent = async () => {
            try {
                const { clientSecret, amount: serverAmount } = await createPaymentIntent(reservation, productId);

                // Utiliser le montant retourné par le serveur s'il existe
                if (serverAmount) {
                    setPaymentAmount(serverAmount);
                }

                setClientSecret(clientSecret);
            } catch (err) {
                console.error("Erreur lors de la création de l'intention de paiement:", err);
                const errorMessage = err instanceof Error ? err.message : "Une erreur est survenue";
                setError(errorMessage);
                onPaymentError(errorMessage);
            }
        };

        if (reservation && productId) {
            fetchPaymentIntent();
        }
    }, [productId, reservation, onPaymentError]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!stripe || !elements) {
            setError("Stripe n'est pas initialisé");
            return;
        }

        if (!clientSecret) {
            setError("Impossible de procéder au paiement sans client secret");
            return;
        }

        if (!reservation) {
            setError("Données de réservation manquantes");
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            // Utiliser notre service de paiement pour confirmer le paiement avec Stripe
            const result = await confirmCardPayment(
                stripe,
                elements,
                clientSecret,
                reservation
            );

            if (result.success && result.paymentIntentId) {
                onPaymentSuccess(result.paymentIntentId);
            } else {
                throw new Error(result.error || "Échec du paiement");
            }
        } catch (err) {
            console.error('Erreur de paiement:', err);
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
            setError(errorMessage);
            onPaymentError(errorMessage);
        } finally {
            setProcessing(false);
        }
    };

    if (!clientSecret && !error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Préparation du paiement...
                </Typography>
            </Box>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>
                Informations de paiement
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <CardElementContainer>
                <CardElement options={cardElementOptions} />
            </CardElementContainer>

            <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    * Pour les tests, utilisez le numéro de carte 4242 4242 4242 4242, une date d'expiration future
                    et n'importe quel CVC à 3 chiffres.
                </Typography>
            </Box>

            <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!stripe || processing || !clientSecret}
                sx={{ mt: 2 }}
            >
                {processing ? (
                    <>
                        <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                        Traitement...
                    </>
                ) : (
                    `Payer ${paymentAmount}€`
                )}
            </Button>
        </Box>
    );
};

export default StripeCheckoutForm;